import { formatInTimeZone } from 'date-fns-tz';
import { es } from 'date-fns/locale/es';
import { formatCLP } from '@/lib/format';
import { parseWorkshopMetafields } from '@impacta/shared';
import type {
  ShopifyVariant,
  SessionOption,
  RawMetafield,
  WorkshopMetafields,
  ShopifyMediaImage,
  ShopifyImage,
} from './types/storefront';

const WORKSHOP_TZ = 'America/Santiago';

const MONTH_MAP: Record<string, number> = {
  Ene: 0, Feb: 1, Mar: 2, Abr: 3, May: 4, Jun: 5,
  Jul: 6, Ago: 7, Sep: 8, Oct: 9, Nov: 10, Dic: 11,
  ene: 0, feb: 1, mar: 2, abr: 3, may: 4, jun: 5,
  jul: 6, ago: 7, sep: 8, oct: 9, nov: 10, dic: 11,
  Enero: 0, Febrero: 1, Marzo: 2, Abril: 3, Mayo: 4, Junio: 5,
  Julio: 6, Agosto: 7, Septiembre: 8, Octubre: 9, Noviembre: 10, Diciembre: 11,
  enero: 0, febrero: 1, marzo: 2, abril: 3, mayo: 4, junio: 5,
  julio: 6, agosto: 7, septiembre: 8, octubre: 9, noviembre: 10, diciembre: 11,
};

/**
 * Parse a Spanish session display label into a local-system Date.
 * Handles multiple legacy label formats used before the structured metafield.
 */
export function parseSessionDate(label: string): Date | null {
  if (!label) return null;
  const match = label.match(/(\d{1,2})\s+(?:de\s+)?([A-Za-záéíóúñ]+?)(?:,?\s*\d{4})?[\s—–-]+(\d{2}):(\d{2})/);
  if (!match) return null;
  const [, dayStr, monthStr, hourStr, minStr] = match;
  const day = parseInt(dayStr, 10);
  const month = MONTH_MAP[monthStr] ?? MONTH_MAP[monthStr.toLowerCase()];
  if (month === undefined || Number.isNaN(day)) return null;
  const now = new Date();
  let year = now.getFullYear();
  const date = new Date(year, month, day, parseInt(hourStr, 10), parseInt(minStr, 10));
  if (date < now) { year++; date.setFullYear(year); }
  return date;
}

/** Convert a local-system Date into a YYYY-MM-DD string (legacy label fallback). */
function toISODate(date: Date): string {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function getEarliestSessionDate(variants: ShopifyVariant[]): string | null {
  const dates: string[] = [];
  for (const v of variants) {
    // Canonical source: structured ISO datetime from variant metafield.
    // Convert to the Chile calendar date so UTC and offset strings both resolve
    // correctly across midnight (e.g. 02:00 UTC on the 26th is still the 25th in Chile).
    const isoValue = v.sessionDatetime?.value;
    if (isoValue) {
      const dt = new Date(isoValue);
      if (!Number.isNaN(dt.getTime())) {
        dates.push(formatInTimeZone(dt, WORKSHOP_TZ, 'yyyy-MM-dd'));
        continue;
      }
    }
    // Fallback: parse display label (backward compatible with pre-metafield products)
    const session = v.selectedOptions.find((o) => o.name === 'Sesion');
    if (session?.value) {
      const d = parseSessionDate(session.value);
      if (d) dates.push(toISODate(d));
    }
  }
  if (dates.length === 0) return null;
  dates.sort();
  return dates[0];
}

export function mapMetafieldsToWorkshopData(
  metafields: (RawMetafield | null)[]
): WorkshopMetafields {
  const raw = metafields.filter((m): m is RawMetafield => m !== null);
  const parsed = parseWorkshopMetafields(raw);

  return {
    duration: parsed.duration,
    level: parsed.level,
    language: parsed.language,
    locationName: parsed.locationName,
    locationAddress: parsed.locationAddress,
    locationComuna: parsed.locationComuna,
    workshopLocation: parsed.workshopLocation,
    learningOutcomes: parsed.learningOutcomes,
    targetAudience: parsed.targetAudience,
    includes: parsed.includes as unknown as WorkshopMetafields['includes'],
    descriptionLong: parsed.descriptionLong,
    materialsIncluded: parsed.materialsIncluded,
    category: parsed.category,
  };
}

export function flattenMedia(media?: { edges: { node: ShopifyMediaImage }[] }): ShopifyImage[] {
  if (!media?.edges) return [];
  return media.edges.filter((e) => e.node?.image).map((e) => e.node.image);
}

/**
 * Resolve a human-readable session label from a Shopify variant.
 *
 * Single source of truth: the `session_datetime` variant metafield
 * (namespace "impacta", key "session_datetime"). Set by the admin publish
 * flow for every workshop variant. We format it to match the label format
 * used during publishing: "lunes 15 de junio, 2026 — 10:00 hrs".
 *
 * See ADR-0023 for rationale.
 */
function resolveSessionLabel(v: ShopifyVariant): string {
  if (v.sessionDatetime?.value) {
    const dt = new Date(v.sessionDatetime.value);
    if (!Number.isNaN(dt.getTime())) {
      const datePart = formatInTimeZone(dt, WORKSHOP_TZ, "EEEE d 'de' MMMM, yyyy", { locale: es });
      const timePart = formatInTimeZone(dt, WORKSHOP_TZ, 'HH:mm');
      return `${datePart} — ${timePart} hrs`;
    }
  }
  // Fallback: use the variant's selectedOptions "Sesion" label (backward
  // compatible with products published before the session_datetime metafield
  // existed, or manually created in Shopify).
  const session = v.selectedOptions.find((o) => o.name === 'Sesion');
  if (session?.value) return session.value;
  return 'Sesion';
}

export function mapVariantsToSessions(variants: ShopifyVariant[]): SessionOption[] {
  return variants.map((v) => ({
    id: v.id,
    label: resolveSessionLabel(v),
    price: formatCLP(v.price.amount),
    spots: v.quantityAvailable ?? 0,
    available: v.availableForSale,
  }));
}
